import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building, Upload, Wand2, CreditCard as Edit3, Package, Star, ChevronLeft, ChevronRight, Check, X, Plus, Sparkles, FileText, Tag, Euro, Calendar, Globe, Users, BarChart3, Award, MessageSquare, Video, Image as ImageIcon, ThumbsUp, Eye } from 'lucide-react';

const CreatePage: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Étape 1: Identité de l'entreprise
    logo: '',
    companyName: '',
    slogan: '',
    sectors: [] as string[],
    employeeCount: '',
    
    // Étape 2: Description
    useAI: false,
    description: '',
    foundedDate: '',
    
    // Étape 3: Solutions
    solutions: [] as Array<{
      id: string;
      name: string;
      pitch: string;
      tags: string[];
      price: string;
    }>,
    
    // Étape 4: Reviews
    googleReviewLink: '',
    featuredReviews: [] as Array<{
      id: string;
      author: string;
      company: string;
      rating: number;
      comment: string;
      product: string;
      sector: string;
      companySize: string;
      hasVideo: boolean;
      hasImage: boolean;
      companyResponse?: string;
    }>,
    satisfactionRates: {
      quality: 95,
      support: 92,
      easeOfUse: 88,
      valueForMoney: 90,
      recommendation: 94
    }
  });

  const sectors = [
    'Technology', 'Finance', 'Healthcare', 'Education', 'Commerce', 'Industry',
    'Services', 'Real Estate', 'Transport', 'Energy', 'Media', 'Consulting',
    'Marketing', 'HR', 'Legal', 'Agriculture', 'Tourism', 'Sports'
  ];

  const employeeCounts = [
    '1-10 employees', '11-50 employees', '51-200 employees',
    '201-500 employees', '501-1000 employees', '1000+ employees'
  ];

  const availableTags = [
    'AI', 'Automation', 'CRM', 'Analytics', 'Cloud', 'Mobile',
    'SaaS', 'API', 'Integration', 'Security', 'Performance', 'Innovation'
  ];

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSectorToggle = (sector: string) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateAIDescription = () => {
    const aiDescription = `${formData.companyName} is an innovative company ${formData.sectors.length > 0 ? `specialized in ${formData.sectors.slice(0, 2).join(' and ')}` : 'a market leader'}.

We distinguish ourselves through our approach centered on innovation and operational excellence. Our ${formData.employeeCount ? formData.employeeCount.toLowerCase() : 'experienced'} team is committed to delivering high-quality solutions that transform the way our clients work.

${formData.slogan ? `Our mission: "${formData.slogan}"` : 'Our mission is to revolutionize your business experience through cutting-edge technology.'}

Since our founding, we have developed recognized expertise and a trusted relationship with our clients, positioning ourselves as a strategic partner for their growth and digital transformation.`;

    setFormData(prev => ({
      ...prev,
      description: aiDescription
    }));
  };

  const addSolution = () => {
    const newSolution = {
      id: Date.now().toString(),
      name: '',
      pitch: '',
      tags: [],
      price: ''
    };
    setFormData(prev => ({
      ...prev,
      solutions: [...prev.solutions, newSolution]
    }));
  };

  const updateSolution = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      solutions: prev.solutions.map(solution =>
        solution.id === id ? { ...solution, [field]: value } : solution
      )
    }));
  };

  const removeSolution = (id: string) => {
    setFormData(prev => ({
      ...prev,
      solutions: prev.solutions.filter(solution => solution.id !== id)
    }));
  };

  const addFeaturedReview = () => {
    const newReview = {
      id: Date.now().toString(),
      author: '',
      company: '',
      rating: 5,
      comment: '',
      product: '',
      sector: '',
      companySize: '',
      hasVideo: false,
      hasImage: false,
      companyResponse: ''
    };
    setFormData(prev => ({
      ...prev,
      featuredReviews: [...prev.featuredReviews, newReview]
    }));
  };

  const updateReview = (id: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      featuredReviews: prev.featuredReviews.map(review =>
        review.id === id ? { ...review, [field]: value } : review
      )
    }));
  };

  const removeReview = (id: string) => {
    setFormData(prev => ({
      ...prev,
      featuredReviews: prev.featuredReviews.filter(review => review.id !== id)
    }));
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 font-semibold ${
            step <= currentStep 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 border-blue-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-6 h-6" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-20 h-0.5 ${
              step < currentStep ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('vendorPage.create.step1Title')}</h3>
        <p className="text-gray-600">{t('vendorPage.create.step1Subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Logo */}
        <div className="lg:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Upload className="w-4 h-4 inline mr-2" />
            {t('vendorPage.create.companyLogo')}
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors">
            {formData.logo ? (
              <div className="space-y-4">
                <img src={formData.logo} alt="Logo" className="h-20 mx-auto" />
                <button
                  onClick={() => handleChange('logo', '')}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {t('vendorPage.create.removeLogo')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-gray-600">{t('vendorPage.create.dragDropLogo')}</p>
                  <button
                    onClick={() => handleChange('logo', '/logo-color-favicone.png')}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t('vendorPage.create.useDefaultLogo')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Company name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="w-4 h-4 inline mr-1" />
            {t('vendorPage.create.companyName')}
          </label>
          <input
            type="text"
            value={formData.companyName}
            className="form-input bg-gray-100 cursor-not-allowed"
            disabled
          />
          <p className="text-xs text-gray-500 mt-1">{t('vendorPage.create.companyNameHelp')}</p>
        </div>

        {/* Slogan */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Sparkles className="w-4 h-4 inline mr-1" />
            {t('vendorPage.create.slogan')}
          </label>
          <input
            type="text"
            value={formData.slogan}
            onChange={(e) => handleChange('slogan', e.target.value)}
            className="form-input"
            placeholder={t('vendorPage.create.sloganPlaceholder')}
          />
        </div>

        {/* Nombre d'employés */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            {t('vendorPage.create.numberOfEmployees')}
          </label>
          <select
            value={formData.employeeCount}
            onChange={(e) => handleChange('employeeCount', e.target.value)}
            className="form-input"
          >
            <option value="">{t('vendorPage.create.select')}</option>
            {employeeCounts.map(count => (
              <option key={count} value={count}>{count}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Secteurs d'activité */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Tag className="w-4 h-4 inline mr-1" />
          {t('vendorPage.create.industrySectorsLabel', { count: formData.sectors.length })}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {sectors.map(sector => (
            <button
              key={sector}
              type="button"
              onClick={() => handleSectorToggle(sector)}
              className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${
                formData.sectors.includes(sector)
                  ? 'bg-blue-100 border-blue-400 text-blue-800 shadow-md transform scale-105'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{sector}</span>
                {formData.sectors.includes(sector) && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('vendorPage.create.step2Title')}</h3>
        <p className="text-gray-600">{t('vendorPage.create.step2Subtitle')}</p>
      </div>

      {/* Choix du mode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => handleChange('useAI', true)}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            formData.useAI
              ? 'border-purple-500 bg-purple-50 shadow-lg'
              : 'border-gray-200 hover:border-purple-300'
          }`}
        >
          <div className="text-center">
            <Wand2 className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('vendorPage.create.generateWithAi')}</h4>
            <p className="text-sm text-gray-600">{t('vendorPage.create.generateWithAiDesc')}</p>
          </div>
        </button>

        <button
          onClick={() => handleChange('useAI', false)}
          className={`p-6 rounded-xl border-2 transition-all duration-200 ${
            !formData.useAI
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-gray-200 hover:border-blue-300'
          }`}
        >
          <div className="text-center">
            <Edit3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">{t('vendorPage.create.fillManually')}</h4>
            <p className="text-sm text-gray-600">{t('vendorPage.create.fillManuallyDesc')}</p>
          </div>
        </button>
      </div>

      {formData.useAI ? (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-center space-x-3 mb-4">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h4 className="text-lg font-semibold text-purple-900">{t('vendorPage.create.automaticGeneration')}</h4>
            </div>
            <p className="text-purple-800 mb-4">
              {t('vendorPage.create.automaticGenerationDesc')}
            </p>
            <button
              onClick={generateAIDescription}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center space-x-2"
            >
              <Wand2 className="w-5 h-5" />
              <span>{t('vendorPage.create.generateDescription')}</span>
            </button>
          </div>

          {formData.description && (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('vendorPage.create.generatedDescription')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={8}
                className="form-input"
                placeholder={t('vendorPage.create.descriptionWillAppear')}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              {t('vendorPage.create.clearDescription')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={8}
              className="form-input"
              placeholder={t('vendorPage.create.descriptionPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              {t('vendorPage.create.foundingDate')}
            </label>
            <input
              type="date"
              value={formData.foundedDate}
              onChange={(e) => handleChange('foundedDate', e.target.value)}
              className="form-input max-w-xs"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('vendorPage.create.step3Title')}</h3>
        <p className="text-gray-600">{t('vendorPage.create.step3Subtitle')}</p>
      </div>

      <div className="space-y-6">
        {formData.solutions.map((solution, index) => (
          <div key={solution.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900">{t('vendorPage.create.solutionNumber', { index: index + 1 })}</h4>
              <button
                onClick={() => removeSolution(solution.id)}
                className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" />
                  {t('vendorPage.create.solutionName')}
                </label>
                <input
                  type="text"
                  value={solution.name}
                  onChange={(e) => updateSolution(solution.id, 'name', e.target.value)}
                  className="form-input"
                  placeholder={t('vendorPage.create.solutionNamePlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Euro className="w-4 h-4 inline mr-1" />
                  {t('vendorPage.create.price')}
                </label>
                <input
                  type="text"
                  value={solution.price}
                  onChange={(e) => updateSolution(solution.id, 'price', e.target.value)}
                  className="form-input"
                  placeholder={t('vendorPage.create.pricePlaceholder')}
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('vendorPage.create.miniPitch')}
              </label>
              <textarea
                value={solution.pitch}
                onChange={(e) => updateSolution(solution.id, 'pitch', e.target.value)}
                rows={3}
                className="form-input"
                placeholder={t('vendorPage.create.miniPitchPlaceholder')}
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                {t('vendorPage.create.tagsLabel', { count: solution.tags.length })}
              </label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                {availableTags.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      const newTags = solution.tags.includes(tag)
                        ? solution.tags.filter(t => t !== tag)
                        : [...solution.tags, tag];
                      updateSolution(solution.id, 'tags', newTags);
                    }}
                    className={`p-2 text-xs rounded-lg border transition-all duration-200 ${
                      solution.tags.includes(tag)
                        ? 'bg-green-100 border-green-300 text-green-800'
                        : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addSolution}
          className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors group"
        >
          <div className="text-center">
            <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-600 mx-auto mb-2" />
            <span className="text-gray-600 group-hover:text-blue-600 font-medium">
              {t('vendorPage.create.addSolution')}
            </span>
          </div>
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('vendorPage.create.step4Title')}</h3>
        <p className="text-gray-600">{t('vendorPage.create.step4Subtitle')}</p>
      </div>

      {/* Lien Google Reviews */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <Globe className="w-5 h-5 mr-2" />
          {t('vendorPage.create.googleReviewLink')}
        </h4>
        <input
          type="url"
          value={formData.googleReviewLink}
          onChange={(e) => handleChange('googleReviewLink', e.target.value)}
          className="form-input"
          placeholder="https://g.page/r/..."
        />
        <p className="text-sm text-blue-700 mt-2">
          {t('vendorPage.create.googleReviewHelp')}
        </p>
      </div>

      {/* Taux de satisfaction */}
      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <h4 className="text-lg font-semibold text-green-900 mb-6 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          {t('vendorPage.create.satisfactionRate')}
        </h4>

        <div className="space-y-4">
          {Object.entries(formData.satisfactionRates).map(([key, value]) => {
            const labels = {
              quality: t('vendorPage.create.satisfaction.quality'),
              support: t('vendorPage.create.satisfaction.support'),
              easeOfUse: t('vendorPage.create.satisfaction.easeOfUse'),
              valueForMoney: t('vendorPage.create.satisfaction.valueForMoney'),
              recommendation: t('vendorPage.create.satisfaction.recommendation')
            };
            
            return (
              <div key={key}>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {labels[key as keyof typeof labels]}
                  </label>
                  <span className="text-lg font-bold text-green-700">{value}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => handleChange('satisfactionRates', {
                    ...formData.satisfactionRates,
                    [key]: parseInt(e.target.value)
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Avis en vedette */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Star className="w-5 h-5 mr-2 text-yellow-500" />
          {t('vendorPage.create.featuredReviews')}
        </h4>

        <div className="space-y-6">
          {formData.featuredReviews.map((review, index) => (
            <div key={review.id} className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-lg font-semibold text-yellow-900">{t('vendorPage.create.reviewNumber', { index: index + 1 })}</h5>
                <button
                  onClick={() => removeReview(review.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.author')}</label>
                  <input
                    type="text"
                    value={review.author}
                    onChange={(e) => updateReview(review.id, 'author', e.target.value)}
                    className="form-input"
                    placeholder={t('vendorPage.create.authorPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.company')}</label>
                  <input
                    type="text"
                    value={review.company}
                    onChange={(e) => updateReview(review.id, 'company', e.target.value)}
                    className="form-input"
                    placeholder={t('vendorPage.create.companyPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.product')}</label>
                  <input
                    type="text"
                    value={review.product}
                    onChange={(e) => updateReview(review.id, 'product', e.target.value)}
                    className="form-input"
                    placeholder={t('vendorPage.create.productPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.rating')}</label>
                  <select
                    value={review.rating}
                    onChange={(e) => updateReview(review.id, 'rating', parseInt(e.target.value))}
                    className="form-input"
                  >
                    {[5, 4, 3, 2, 1].map(rating => (
                      <option key={rating} value={rating}>
                        {rating} {rating > 1 ? t('vendorPage.create.stars') : t('vendorPage.create.star')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.sector')}</label>
                  <select
                    value={review.sector}
                    onChange={(e) => updateReview(review.id, 'sector', e.target.value)}
                    className="form-input"
                  >
                    <option value="">{t('vendorPage.create.select')}</option>
                    {sectors.map(sector => (
                      <option key={sector} value={sector}>{sector}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.companySize')}</label>
                  <select
                    value={review.companySize}
                    onChange={(e) => updateReview(review.id, 'companySize', e.target.value)}
                    className="form-input"
                  >
                    <option value="">{t('vendorPage.create.select')}</option>
                    {employeeCounts.map(count => (
                      <option key={count} value={count}>{count}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.comment')}</label>
                <textarea
                  value={review.comment}
                  onChange={(e) => updateReview(review.id, 'comment', e.target.value)}
                  rows={3}
                  className="form-input"
                  placeholder={t('vendorPage.create.commentPlaceholder')}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vendorPage.create.companyResponse')}</label>
                <textarea
                  value={review.companyResponse || ''}
                  onChange={(e) => updateReview(review.id, 'companyResponse', e.target.value)}
                  rows={2}
                  className="form-input"
                  placeholder={t('vendorPage.create.companyResponsePlaceholder')}
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={review.hasVideo}
                    onChange={(e) => updateReview(review.id, 'hasVideo', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Video className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-gray-700">{t('vendorPage.create.videoReview')}</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={review.hasImage}
                    onChange={(e) => updateReview(review.id, 'hasImage', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <ImageIcon className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-700">{t('vendorPage.create.withImage')}</span>
                </label>
              </div>
            </div>
          ))}

          <button
            onClick={addFeaturedReview}
            className="w-full p-6 border-2 border-dashed border-yellow-300 rounded-xl hover:border-yellow-400 hover:bg-yellow-50 transition-colors group"
          >
            <div className="text-center">
              <Plus className="w-8 h-8 text-yellow-400 group-hover:text-yellow-600 mx-auto mb-2" />
              <span className="text-yellow-600 group-hover:text-yellow-700 font-medium">
                {t('vendorPage.create.addFeaturedReview')}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('vendorPage.create.previewTitle')}</h3>
        <p className="text-gray-600">{t('vendorPage.create.previewSubtitle')}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-lg">
        {/* Header de la page */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8">
          <div className="flex items-center space-x-6">
            {formData.logo && (
              <img src={formData.logo} alt="Logo" className="h-16 w-16 bg-white rounded-lg p-2" />
            )}
            <div>
              <h1 className="text-3xl font-bold">{formData.companyName}</h1>
              {formData.slogan && (
                <p className="text-xl text-blue-100 mt-2">{formData.slogan}</p>
              )}
              <div className="flex items-center space-x-4 mt-3">
                {formData.employeeCount && (
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                    {formData.employeeCount}
                  </span>
                )}
                {formData.sectors.slice(0, 3).map(sector => (
                  <span key={sector} className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                    {sector}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {formData.description && (
          <div className="p-8 border-b">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('vendorPage.create.aboutUs')}</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{formData.description}</p>
            {formData.foundedDate && (
              <p className="text-sm text-gray-500 mt-4">
                {t('vendorPage.create.foundedIn', { year: new Date(formData.foundedDate).getFullYear() })}
              </p>
            )}
          </div>
        )}

        {/* Solutions */}
        {formData.solutions.length > 0 && (
          <div className="p-8 border-b">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('vendorPage.create.ourSolutions')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {formData.solutions.map(solution => (
                <div key={solution.id} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">{solution.name}</h3>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {solution.price}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-4">{solution.pitch}</p>
                  <div className="flex flex-wrap gap-2">
                    {solution.tags.map(tag => (
                      <span key={tag} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Satisfaction rates */}
        <div className="p-8 border-b">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('vendorPage.create.customerSatisfaction')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(formData.satisfactionRates).map(([key, value]) => {
              const labels = {
                quality: t('vendorPage.create.satisfactionShort.quality'),
                support: t('vendorPage.create.satisfactionShort.support'),
                easeOfUse: t('vendorPage.create.satisfactionShort.easeOfUse'),
                valueForMoney: t('vendorPage.create.satisfactionShort.valueForMoney'),
                recommendation: t('vendorPage.create.satisfactionShort.recommendation')
              };
              
              return (
                <div key={key} className="text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">{value}%</div>
                  <div className="text-sm text-gray-600">{labels[key as keyof typeof labels]}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer reviews */}
        {formData.featuredReviews.length > 0 && (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('vendorPage.create.customerReviews')}</h2>
            <div className="space-y-6">
              {formData.featuredReviews.slice(0, 3).map(review => (
                <div key={review.id} className="bg-yellow-50 rounded-lg p-6 border border-yellow-200">
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        <span className="font-semibold text-gray-900">{review.author}</span>
                        <span className="text-gray-500">- {review.company}</span>
                        {review.hasVideo && <Video className="w-4 h-4 text-blue-600" />}
                        {review.hasImage && <ImageIcon className="w-4 h-4 text-green-600" />}
                      </div>
                      <p className="text-gray-700 italic">"{review.comment}"</p>
                      {review.companyResponse && (
                        <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-400">
                          <p className="text-sm text-blue-800">
                            <strong>{t('vendorPage.create.responseFrom', { company: formData.companyName })}</strong> {review.companyResponse}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {formData.googleReviewLink && (
              <div className="mt-6 text-center">
                <a
                  href={formData.googleReviewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ThumbsUp className="w-5 h-5" />
                  <span>{t('vendorPage.create.leaveReviewOnGoogle')}</span>
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.companyName && formData.sectors.length > 0;
      case 2:
        return formData.description.length > 0;
      case 3:
        return formData.solutions.length > 0 && formData.solutions.every(s => s.name && s.pitch);
      case 4:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('vendorPage.create.pageHeader')}
          </h1>
          <p className="text-xl text-gray-600">
            {t('vendorPage.create.pageSubtitle')}
          </p>
        </div>

        {renderStepIndicator()}

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Aperçu */}
        {currentStep === 4 && (
          <div className="mb-8">
            {renderPreview()}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="flex items-center space-x-2 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{t('vendorPage.create.previous')}</span>
          </button>

          <div className="flex items-center space-x-4">
            {currentStep === 4 && (
              <button className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <Eye className="w-5 h-5" />
                <span>{t('vendorPage.create.publishPage')}</span>
              </button>
            )}

            {currentStep < 4 && (
              <button
                onClick={handleNext}
                disabled={!isStepValid()}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{t('vendorPage.create.next')}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePage;